import { useEffect, useMemo, useRef, useState } from 'react';
import { DeleteDusbinIcon, UploadIconV2 } from '../assets/icons';
import styles from './UploadFileModal.module.scss';
import {
  ACCEPT_3D_MODELS_IN_ZIP,
  PRODUCT_IMAGES_OPTIONS,
  PRODUCT_VIDEOS_OPTIONS,
  PRODUCT_PAGE_GIF_SUPPORTED_OPTION,
  DIMENSION_SHOT_OPTIONS,
  ACCEPT_3D_MODELS,
} from '../constants/constants';
import { BigButton, ModalButtonArea } from '../common';
import cx from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { ElipsisTooltip } from '../common/ElipsisTooltip';
import ReactSelect from 'react-select';
import { modifierSmall, reactSelectStyles } from '../helpers/reactSelectStyles';
import { getExtension } from './3DModelUpload/Upload3DModalButton';
import { errorToast } from '../helpers/toastHelper';
import {
  selectAvailable3DModelUploadLimit,
  selectAvailable3DModelVariationsWithoutDivide,
  selectAvailableTextures,
} from '../redux/slicers/subscriptionSlicer';
import { useSelector } from 'react-redux';
import { useUserContext } from '../contexts/UserContext';
import {
  ADMIN_DASHBOARD,
  ADMIN_MY_PRODUCTS_3DMODAL_V2,
  COMMON_HOME,
} from '../constants/routePathConstants';
import { Mixpanel, MxEvent } from '../common/Mixpanel';
import { getProductPageType } from '../helpers/jsHelper';

const UploadFileModal = ({
  showModal,
  onClose,
  title,
  acceptedFiles,
  handleFileUpload,
  supportZip,
  supportedFilesText,
  showUploadedFiles,
  uploadType,
  isTexture,
}) => {
  const { isNewSubscriptionPlan } = useUserContext();
  const availableTextures = useSelector(selectAvailableTextures);
  const available3DModelVariations = useSelector(selectAvailable3DModelVariationsWithoutDivide);
  const available3DModelUploadLimit = useSelector(selectAvailable3DModelUploadLimit);

  useEffect(() => {
    if (showModal) Mixpanel.track(true, MxEvent.POPUP_UPLOAD_YOUR_FILE);
  }, [showModal]);

  const availableModels = useMemo(
    () => (isNewSubscriptionPlan ? available3DModelUploadLimit : available3DModelVariations),
    [isNewSubscriptionPlan, available3DModelUploadLimit, available3DModelVariations]
  );

  const dropZoneRef = useRef();
  const [files, setFiles] = useState([]);

  const handleDeleteFile = (id) => {
    setFiles(files?.filter((_item) => _item?.id !== id));
  };

  const _handleFileUpload = (f) => {
    Array.from(f)?.forEach((file) => {
      const ext = getExtension(file?.name);
      Mixpanel.track(true, MxEvent.FILE_UPLOAD_START, {
        file_type: ACCEPT_3D_MODELS.includes(`.${ext}`) ? 'Model' : 'Texture',
        file_extension: ext,
        file_name: file?.name?.split('.')?.[0],
      });
    });
    handleFileUpload(f);
  };

  const handleFileChange = async (_files) => {
    if (showUploadedFiles) {
      const __files = Array.from(_files)?.filter((_item) =>
        acceptedFiles?.includes(getExtension(_item?.name))
      );

      if (!__files.length) {
        errorToast('The uploaded file formats are not supported');
      }

      setFiles([
        ...files,
        ...__files?.map((_item) => {
          const isGIF = _item?.type?.includes('gif');
          const isDimensionShotUpload = uploadType === getProductPageType().DIMENSIONSHOT;
          if (isDimensionShotUpload) {
            _item.option = DIMENSION_SHOT_OPTIONS;
          } else if (isGIF) {
            _item.option = PRODUCT_PAGE_GIF_SUPPORTED_OPTION;
          }
          _item.id = uuidv4();
          return _item;
        }),
      ]);
    } else _handleFileUpload(_files);
  };

  const SelectTitle = ({ file }) => {
    const isGIF = file?.type?.includes('gif');
    const isDimensionShotUpload = uploadType === getProductPageType().DIMENSIONSHOT;
    let options = file?.type?.includes('video') ? PRODUCT_VIDEOS_OPTIONS : PRODUCT_IMAGES_OPTIONS;

    if (isTexture || uploadType === getProductPageType().PRODUCTIMAGE) {
      options = options.filter((_item) => _item?.value !== DIMENSION_SHOT_OPTIONS?.value);
    }

    if (isGIF) {
      options = PRODUCT_PAGE_GIF_SUPPORTED_OPTION;
    }

    return (
      <ReactSelect
        options={options}
        isSearchable={false}
        value={isDimensionShotUpload ? DIMENSION_SHOT_OPTIONS : file.option}
        maxMenuHeight={200}
        placeholder="Section Title"
        isDisabled={isDimensionShotUpload || isGIF}
        onChange={(value) => {
          const _files = files?.map((_item) => {
            if (_item.id === file.id) file.option = value;
            return _item;
          });
          setFiles(_files);
        }}
        menuPlacement="auto" // Automatically place the dropdown based on space
        // menuPortalTarget={document.querySelector(`.${styles.body}`)} // Render the dropdown outside the container
        menuPosition="fixed"
        styles={{
          ...reactSelectStyles,
          ...modifierSmall,
          menu: (base) => ({
            ...base,
            width: window.innerWidth < 600 ? '80px' : '160px',
            borderRadius: '8px',
            zIndex: 9999,
          }),
          control: (base) => ({
            ...base,
            width: window.innerWidth < 600 ? '80px' : '160px',
          }),
          container: (base) => ({
            ...base,
            position: 'relative',
          }),
        }}
      />
    );
  };
  return (
    <>
      <ModalButtonArea
        showModal={showModal}
        size="lg"
        centered
        className={cx('texture-upload-modal keep-text', styles.dialog)}
        contentClassName={cx(
          styles.modalContent,
          showUploadedFiles && files?.length ? styles.minHeight100 : ''
        )}
        title={
          <ElipsisTooltip title={title}>
            {(props) => (
              <span className={styles.modalTitle} {...props}>
                {title}
              </span>
            )}
          </ElipsisTooltip>
        }
        onCloseModal={onClose}
        isCloseModal
        backdrop="static"
        body={
          <div className="d-flex gap-3 flex-column align-items-center justify-content-center w-100">
            <div
              ref={dropZoneRef}
              className={cx(
                styles.dropZone,
                showUploadedFiles && files?.length ? styles.filesSelected : ''
              )}
              onDragOver={() => dropZoneRef?.current?.classList?.add(styles.dragOver)}
              onDragLeave={() => dropZoneRef?.current?.classList?.remove(styles.dragOver)}>
              <input
                onDrop={(event) => handleFileChange(event.dataTransfer.files)}
                onChange={(event) => handleFileChange(event.target.files)}
                onClick={(e) => (e.target.value = null)}
                type="file"
                multiple
                accept={acceptedFiles}
              />
              <div className="d-flex align-items-center flex-column gap-2 justify-content-center h-100 p-2 text-center">
                <div className={styles.uploadIcon}>
                  <UploadIconV2 />
                </div>
                <span className={styles.title}>Drag & Drop file here or Choose File</span>
                <span className={styles.subTitle}>
                  {`(Supported file formats: ${supportedFilesText})`}
                </span>
                {supportZip ? (
                  <span className={styles.subTitle}>
                    (Zip supported formats: {ACCEPT_3D_MODELS_IN_ZIP.split(',').join(',')})
                  </span>
                ) : null}
              </div>
            </div>

            {files?.length === 0 &&
            [COMMON_HOME, ADMIN_DASHBOARD, ADMIN_MY_PRODUCTS_3DMODAL_V2].includes(
              window.location.pathname
            ) ? (
              <div
                className={
                  'w-100 d-flex align-items-center justify-content-between gap-2 flex-wrap'
                }>
                <p className={styles.text}>
                  Model uploads available: <span>{availableModels}</span>
                </p>
                {ADMIN_MY_PRODUCTS_3DMODAL_V2 !== window.location.pathname ? (
                  <p className={styles.text}>
                    Texture uploads available: <span>{availableTextures}</span>
                  </p>
                ) : null}
              </div>
            ) : null}

            {files?.length ? (
              <div className="w-100 d-flex gap-3 flex-column">
                <div className="w-100 d-flex gap-3 flex-column">
                  <div className={styles.head}>
                    <span className={styles.w60}>Name</span>
                    <span className={styles.w40}>Add to</span>
                  </div>

                  <div className={styles.body}>
                    {files?.map((file) => (
                      <div className={styles.bodyItem}>
                        <ElipsisTooltip title={file.name}>
                          {(props) => (
                            <span className={styles.w60} {...props}>
                              {file.name}
                            </span>
                          )}
                        </ElipsisTooltip>
                        <div className={`${styles.w40} d-flex justify-content-between`}>
                          <SelectTitle file={file} />
                          <span
                            onClick={() => handleDeleteFile(file?.id)}
                            className="d-flex align-items-center">
                            <DeleteDusbinIcon />
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between w-100">
                  <span>{`${files?.length} ${files?.length > 1 ? 'files' : 'file'} selected`}</span>
                  {/* <ElipsisTooltip alwaysShow={files?.find(file => !file?.option)} title={files?.find(file => !file?.option) ? "Please select section for all files." : ''}>
                                    {props =>
                                        <div {...props}>
                                            <BigButton className={styles.uploadButton} disabled={files?.find(file => !file?.option)} onClick={() => handleFileUpload(files)} >Upload</BigButton>
                                        </div>
                                    }

                                </ElipsisTooltip> */}
                  <BigButton
                    className={styles.uploadButton}
                    disabled={files?.find((file) => !file?.option)}
                    onClick={() => _handleFileUpload(files)}>
                    Upload
                  </BigButton>
                </div>
              </div>
            ) : null}
          </div>
        }
      />
    </>
  );
};

export default UploadFileModal;
